import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Button, Form, Modal, InputGroup, Spinner, Alert, Card } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit, faBan, faCheck, faSearch } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

export function LimiteBouleTable({ targetLevel, title, description, triggerNewBlockage, mode = 'limite' }) {
    const [rules, setRules] = useState([]);
    const [targets, setTargets] = useState([]); 
    const [draws, setDraws] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    
    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedNumbers, setSelectedNumbers] = useState([]);
    const manualInputRef = useRef(null);
    
    const DEFAULT_CONFIG = {
        target_level: targetLevel === 'TIRAGE' ? 'GENERAL' : targetLevel,
        agent: '', branch: '',
        draw: '', 
        game_type: 'BORLETTE',
        number: '',
        limit_amount: mode === 'blocage' ? '0' : '',
        is_blocked: mode === 'blocage',
        is_active: true,
    };
    
    const [formData, setFormData] = useState(DEFAULT_CONFIG);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const apiTargetLevel = targetLevel === 'TIRAGE' ? 'GENERAL' : targetLevel;
            const promises = [
                client(`risk/number-overrides/?target_level=${apiTargetLevel}`),
                client("draw-configs/") // To get draws
            ];

            if (targetLevel === 'AGENT') {
                promises.push(client("agents/")); 
            } else if (targetLevel === 'BRANCH') {
                promises.push(client("branches/"));
            }

            const results = await Promise.all(promises);
            let rulesList = results[0]?.results || results[0] || [];
            
            if (targetLevel === 'GENERAL') {
                rulesList = rulesList.filter(r => !r.draw);
            } else if (targetLevel === 'TIRAGE') {
                rulesList = rulesList.filter(r => !!r.draw);
            }

            if (mode === 'blocage') {
                rulesList = rulesList.filter(r => r.is_blocked === true);
            } else {
                rulesList = rulesList.filter(r => r.is_blocked === false);
            }

            setRules(rulesList);
            
            const rawDraws = Array.isArray(results[1]) ? results[1] : (results[1]?.results || results[1]?.draws || []);
            setDraws(rawDraws);

            if (targetLevel !== 'TENANT' && targetLevel !== 'GENERAL' && targetLevel !== 'TIRAGE') {
                setTargets(Array.isArray(results[2]) ? results[2] : (results[2]?.results || []));
            }
            setFetchError(null);
        } catch (e) { 
            console.error(e); 
            setFetchError(e?.message || e?.detail || "Erreur de chargement des données API.");
        }
        setLoading(false);
    }, [targetLevel, mode]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (triggerNewBlockage > 0) {
            handleOpenModal();
        }
    }, [triggerNewBlockage]);

    const handleOpenModal = (rule = null) => {
        if (rule) {
            setFormData({ ...rule, draw: rule.draw || '' });
            setIsEditing(true);
            setSelectedNumbers([rule.number]);
        } else {
            const firstDrawId = draws.length > 0 ? (draws[0].draw?.id || draws[0].id || '') : '';
            setFormData({ 
                ...DEFAULT_CONFIG, 
                draw: targetLevel === 'TIRAGE' ? firstDrawId : '' 
            });
            setIsEditing(false);
            setSelectedNumbers([]);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field === 'game_type') {
            setSelectedNumbers([]);
            setMariageStep(1);
            setMariageFirst(null);
        }
    };

    // ─── Mariage combo builder ────────────────────────────────────
    const [mariageStep, setMariageStep] = useState(1); 
    const [mariageFirst, setMariageFirst] = useState(null);

    const handleMariagePick = (num) => {
        const s = num.toString().padStart(2, '0');
        if (mariageStep === 1) {
            setMariageFirst(s);
            setMariageStep(2);
        } else {
            const combo = mariageFirst + '-' + s;
            setSelectedNumbers(prev => {
                if (prev.includes(combo)) return prev; 
                return [...prev, combo];
            });
            setMariageStep(1);
            setMariageFirst(null);
        }
    };

    const cancelMariageStep = () => {
        setMariageStep(1);
        setMariageFirst(null);
    };

    const formatMariageDisplay = (combo) => {
        if (!combo || combo.length !== 5) return combo;
        return `${combo.slice(0, 2)} × ${combo.slice(3, 5)}`;
    };

    const toggleNumber = (num) => {
        const s = num.toString().padStart(2, '0');
        setSelectedNumbers(prev => 
            prev.includes(s) ? prev.filter(n => n !== s) : [...prev, s]
        );
    };

    const quickSelect = (type) => {
        const numbers = [];
        for (let i = 0; i < 100; i++) {
            const s = i.toString().padStart(2, '0');
            if (type === 'all') numbers.push(s);
            if (type === 'doubles' && i % 11 === 0) numbers.push(s); 
            if (type === 'even' && i % 2 === 0) numbers.push(s);     
            if (type === 'odd' && i % 2 !== 0) numbers.push(s);
            if (type.startsWith('end-')) {
                const end = type.split('-')[1];
                if (s.endsWith(end)) numbers.push(s);
            }
            if (type.startsWith('start-')) {
                const start = type.split('-')[1];
                if (s.startsWith(start)) numbers.push(s);
            }
        }
        setSelectedNumbers(numbers);
    };

    const handleAddManualNumbers = () => {
        const input = manualInputRef.current;
        if (!input) return;
        const val = input.value.trim();
        if (!val) return;
        const nums = val.split(/[,\s]+/)
            .map(n => n.trim())
            .filter(n => n.length > 0)
            .map(n => {
                if (formData.game_type !== 'MARIAGE') {
                    return n.replace(/[^0-9]/g, '');
                }
                return n;
            });
        setSelectedNumbers(prev => [...new Set([...prev, ...nums])]);
        input.value = '';
    };

    const saveRule = async () => {
        if (targetLevel === 'AGENT' && !formData.agent && !isEditing) return alert("Veuillez sélectionner un agent");
        if (targetLevel === 'BRANCH' && !formData.branch && !isEditing) return alert("Veuillez sélectionner une succursale");
        if (targetLevel === 'TIRAGE' && !formData.draw) return alert("Veuillez sélectionner un tirage");
        if (selectedNumbers.length === 0) return alert("Veuillez sélectionner au moins un numéro");
        if (mode === 'limite' && (!formData.limit_amount || Number(formData.limit_amount) <= 0)) {
            setValidationError("La limite doit être supérieure à 0 HTG.");
            return;
        }
        
        setSaving(true);
        setValidationError(null);
        try {
            const basePayload = { ...formData };
            if (targetLevel !== 'AGENT') basePayload.agent = null;
            if (targetLevel !== 'BRANCH') basePayload.branch = null;
            if (basePayload.draw === '') basePayload.draw = null;
            
            if (!basePayload.limit_amount || basePayload.limit_amount === '') {
                basePayload.limit_amount = '0';
            }

            if (isEditing) {
                await client(`risk/number-overrides/${basePayload.id}/`, { 
                    method: "PUT", 
                    body: { ...basePayload, number: selectedNumbers[0] } 
                });
            } else {
                await client(`risk/number-overrides/batch/`, { 
                    method: "POST", 
                    body: { 
                        numbers: selectedNumbers,
                        data: basePayload
                    } 
                });
            }
            await load();
            handleCloseModal();
        } catch (e) {
            console.error(e);
            setValidationError(e?.detail || e?.message || "Une erreur est survenue lors de l'enregistrement.");
        }
        setSaving(false);
    };

    const deleteRule = async (id) => {
        if (!window.confirm(`Supprimer ${mode === 'blocage' ? 'ce blocage' : 'cette limite'} ?`)) return;
        
        setSaving(true);
        try {
            await client(`risk/number-overrides/${id}/`, { method: "DELETE" });
            await load();
        } catch (e) { 
            console.error(e);
            alert("Erreur lors de la suppression");
        }
        setSaving(false);
    };
    
    const toggleStatus = async (rule) => {
        try {
            await client(`risk/number-overrides/${rule.id}/`, { 
                method: "PUT", 
                body: { ...rule, is_active: !rule.is_active } 
            });
            setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !rule.is_active } : r));
        } catch (e) { 
            console.error(e); 
            alert("Erreur lors du changement de statut");
        }
    };

    const getTargetName = (rule) => {
        if (targetLevel === 'GENERAL' || targetLevel === 'TIRAGE') return "Général";
        
        let targetId = targetLevel === 'AGENT' ? rule.agent : rule.branch;
        const targetObj = targets.find(t => t.id === targetId);
        
        if (!targetObj) return rule.agent_name || rule.branch_name || `ID: ${targetId}`;

        if (targetLevel === 'AGENT') return targetObj.username || `${targetObj.first_name} ${targetObj.last_name}`;
        return targetObj.name;
    };

    const normalizedSearch = searchQuery.trim().toLowerCase();
    const filteredRules = rules.filter(rule => {
        if (!normalizedSearch) return true;
        return [
            getTargetName(rule),
            rule.draw_name || "Tous",
            rule.game_type_display || rule.game_type,
            rule.number,
            rule.limit_amount
        ].some(value => String(value || "").toLowerCase().includes(normalizedSearch));
    });

    const exportRows = () => {
        const header = ["Cible", "Tirage", "Type", "Numéro", "Limite", "Statut"];
        const rows = filteredRules.map(rule => [
            getTargetName(rule),
            rule.draw_name || "Tous",
            rule.game_type_display || rule.game_type,
            rule.number,
            mode === 'limite' ? `${Number(rule.limit_amount).toLocaleString()} HTG` : "Bloqué",
            rule.is_active ? "Actif" : "Inactif"
        ]);
        return [header, ...rows];
    };

    const copyTable = async () => {
        const text = exportRows().map(row => row.join("\t")).join("\n");
        try {
            await navigator.clipboard.writeText(text);
        } catch (e) {
            console.error(e);
            alert("La copie n'est pas disponible dans ce navigateur.");
        }
    };

    const downloadCsv = () => {
        const csv = exportRows()
            .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(";"))
            .join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `limites-boules-${targetLevel.toLowerCase()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (fetchError) {
        return (
            <Alert variant="danger" className="text-center p-4">
                <h6 className="fw-bold mb-2">Erreur Opérationnelle</h6>
                <p className="small mb-3">{fetchError}</p>
                <Button onClick={load} variant="outline-danger" size="sm">Réessayer</Button>
            </Alert>
        );
    }

    return (
        <div>
            <div className="border rounded" style={{ borderColor: '#EAEAEA', overflow: 'hidden' }}>


                {/* DataTables Toolbar */}
                <div className="px-3 py-3 d-flex justify-content-between align-items-center flex-wrap bg-white border-bottom" style={{ gap: '12px' }}>
                    <div className="d-flex flex-wrap" style={{ gap: '5px' }}>
                        <Button variant="light" size="sm" onClick={copyTable} style={{ backgroundColor: '#E9ECEF', borderColor: '#CED4DA', color: '#333', fontSize: '0.78rem', fontWeight: '500', borderRadius: '3px', padding: '5px 12px' }}>COPIER</Button>
                        <Button variant="light" size="sm" onClick={downloadCsv} style={{ backgroundColor: '#E9ECEF', borderColor: '#CED4DA', color: '#333', fontSize: '0.78rem', fontWeight: '500', borderRadius: '3px', padding: '5px 12px' }}>EXCEL</Button>
                        <Button variant="light" size="sm" onClick={() => window.print()} style={{ backgroundColor: '#E9ECEF', borderColor: '#CED4DA', color: '#333', fontSize: '0.78rem', fontWeight: '500', borderRadius: '3px', padding: '5px 12px' }}>PDF</Button>
                        <Button variant="light" size="sm" onClick={() => window.print()} style={{ backgroundColor: '#E9ECEF', borderColor: '#CED4DA', color: '#333', fontSize: '0.78rem', fontWeight: '500', borderRadius: '3px', padding: '5px 12px' }}>IMPRIMER</Button>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        <label className="mb-0 fw-bold small text-dark" style={{ fontSize: '0.85rem' }}>Search:</label>
                        <Form.Control
                            size="sm"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{ width: '180px', borderRadius: '3px', borderColor: '#CED4DA' }}
                        />
                    </div>
                </div>

                {/* DataTables Table */}
                <div className="table-responsive">
                    <Table bordered hover className="align-middle mb-0" style={{ borderColor: "#DDD", borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: "#FFF" }}>
                                <th style={{ fontSize: "0.9rem", fontWeight: "bold", padding: '10px 14px', borderBottom: '2px solid #333' }}>
                                    Tirage <span style={{ fontSize: '0.7rem', color: '#888' }}>▲▼</span>
                                </th>
                                <th style={{ fontSize: "0.9rem", fontWeight: "bold", padding: '10px 14px', borderBottom: '2px solid #333' }}>
                                    Boule <span style={{ fontSize: '0.7rem', color: '#888' }}>▲▼</span>
                                </th>
                                <th style={{ fontSize: "0.9rem", fontWeight: "bold", padding: '10px 14px', borderBottom: '2px solid #333' }}>
                                    Limite <span style={{ fontSize: '0.7rem', color: '#888' }}>▲▼</span>
                                </th>
                                <th style={{ width: "120px", padding: '10px 14px', borderBottom: '2px solid #333' }} className="text-end">
                                    <span style={{ fontSize: '0.7rem', color: '#888' }}>▼</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRules.map((rule) => (
                                <tr key={rule.id}>
                                    <td style={{ color: "#333", fontSize: "0.9rem", padding: '10px 14px' }}>
                                        {rule.draw_name || "tout"}
                                    </td>
                                    <td style={{ color: "#333", fontSize: "0.9rem", padding: '10px 14px' }}>
                                        {rule.number}
                                    </td>
                                    <td style={{ color: "#333", fontSize: "0.9rem", padding: '10px 14px' }}>
                                        {rule.is_blocked ? "—" : Math.round(Number(rule.limit_amount) || 0)}
                                    </td>
                                    <td className="text-end" style={{ padding: '10px 14px' }}>
                                        <span 
                                            style={{ color: '#DC3545', cursor: 'pointer', fontSize: '0.88rem' }}
                                            onClick={() => deleteRule(rule.id)}
                                        >
                                            Supprimer
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {filteredRules.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted small">
                                        No data available in table
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* DataTables Footer */}
                <div className="px-3 py-3 border-top d-flex justify-content-between align-items-center bg-white flex-wrap" style={{ gap: '12px' }}>
                    <span className="small text-muted" style={{ fontSize: '0.85rem' }}>
                        Showing 1 to {filteredRules.length} of {filteredRules.length} entries
                    </span>
                    <div className="d-flex align-items-center" style={{ gap: '2px' }}>
                        <Button variant="light" size="sm" disabled style={{ backgroundColor: '#F8F9FA', borderColor: '#DDD', color: '#999', fontSize: '0.8rem', borderRadius: '3px 0 0 3px', padding: '4px 10px' }}>Previous</Button>
                        <Button variant="secondary" size="sm" style={{ backgroundColor: '#D6D6D6', borderColor: '#CCC', color: '#222', fontSize: '0.8rem', borderRadius: '0', padding: '4px 12px', fontWeight: 'bold' }}>1</Button>
                        <Button variant="light" size="sm" disabled style={{ backgroundColor: '#F8F9FA', borderColor: '#DDD', color: '#999', fontSize: '0.8rem', borderRadius: '0 3px 3px 0', padding: '4px 10px' }}>Next</Button>
                    </div>
                </div>
            </div>

            {/* Modal: "Ajouter limite" matching Image 1 */}
            <Modal show={openModal} onHide={handleCloseModal} centered style={{ backdropFilter: 'blur(1px)' }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #EAEAEA', padding: '16px 20px' }}>
                    <Modal.Title style={{ fontWeight: '600', fontSize: '1.25rem', color: '#222' }}>
                        {isEditing ? "Modifier limite" : "Ajouter limite"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px 12px 24px' }}>
                    {validationError && (
                        <Alert variant="danger" className="py-2 px-3 mb-3 small" onClose={() => setValidationError(null)} dismissible>
                            {validationError}
                        </Alert>
                    )}

                    
                    {/* Target Dropdown (Agent/Branch) */}
                    {(targetLevel === 'AGENT' || targetLevel === 'BRANCH') && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small text-dark mb-1">
                                {targetLevel === 'AGENT' ? 'Agent' : 'Succursale'}
                            </Form.Label>
                            <Form.Select disabled={isEditing}
                                value={targetLevel === 'AGENT' ? (formData.agent || "") : (formData.branch || "")}
                                onChange={e => handleChange(targetLevel.toLowerCase(), e.target.value)}
                                style={{
                                    borderColor: '#28A745',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    height: '38px'
                                }}
                            >
                                <option value="">Sélectionnez {targetLevel === 'AGENT' ? 'un agent' : 'une succursale'}</option>
                                {targets.map(t => (
                                    <option key={t.id} value={t.id}>{t.name || t.username}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* Tirage Dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-dark mb-1">Tirage</Form.Label>
                        <div className="position-relative d-flex align-items-center">
                            <Form.Select
                                value={formData.draw || ""}
                                onChange={e => handleChange('draw', e.target.value)}
                                style={{
                                    borderColor: '#28A745',
                                    borderRadius: '4px',
                                    paddingRight: '36px',
                                    fontSize: '0.9rem',
                                    height: '38px'
                                }}
                            >
                                <option value="">Tout</option>
                                {draws.map(d => {
                                    const drawId = d.draw?.id || d.id;
                                    const drawName = d.draw?.name || d.name;
                                    return <option key={drawId} value={drawId}>{drawName}</option>
                                })}
                            </Form.Select>
                            <FontAwesomeIcon
                                icon={faCheck}
                                className="position-absolute text-success"
                                style={{ right: '28px', pointerEvents: 'none', fontSize: '0.9rem' }}
                            />
                        </div>
                    </Form.Group>

                    {/* Side-by-side: Boule & Limite */}
                    <div className="row g-2 mb-3">
                        <div className="col-6">
                            <Form.Label className="fw-bold small text-dark mb-1">Boule</Form.Label>
                            <div className="position-relative d-flex align-items-center">
                                <Form.Control
                                    type="text"
                                    placeholder=""
                                    value={selectedNumbers.join(', ')}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        setSelectedNumbers(raw ? raw.split(/[,\s]+/) : []);
                                    }}
                                    style={{
                                        borderColor: selectedNumbers.length > 0 ? '#DC3545' : '#CED4DA',
                                        borderRadius: '4px',
                                        paddingRight: '30px',
                                        fontSize: '0.9rem',
                                        height: '38px'
                                    }}
                                />
                                {selectedNumbers.length > 0 && (
                                    <span
                                        onClick={() => setSelectedNumbers([])}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            color: '#DC3545',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            lineHeight: 1
                                        }}
                                    >
                                        &times;
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="col-6">
                            <Form.Label className="fw-bold small text-dark mb-1">Limite</Form.Label>
                            <div className="position-relative d-flex align-items-center">
                                <Form.Control
                                    type="number"
                                    placeholder=""
                                    value={formData.limit_amount}
                                    onChange={e => handleChange('limit_amount', e.target.value)}
                                    style={{
                                        borderColor: formData.limit_amount ? '#DC3545' : '#CED4DA',
                                        borderRadius: '4px',
                                        paddingRight: '30px',
                                        fontSize: '0.9rem',
                                        height: '38px'
                                    }}
                                />
                                {formData.limit_amount !== '' && (
                                    <span
                                        onClick={() => handleChange('limit_amount', '')}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            color: '#DC3545',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            lineHeight: 1
                                        }}
                                    >
                                        &times;
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Blue full-width button "ajouter" */}
                    <Button
                        variant="primary"
                        className="w-100 mb-3"
                        disabled={saving}
                        onClick={saveRule}
                        style={{
                            backgroundColor: '#007BFF',
                            borderColor: '#007BFF',
                            borderRadius: '4px',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            padding: '8px 0',
                            textTransform: 'lowercase'
                        }}
                    >
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                enregistrement...
                            </>
                        ) : "ajouter"}
                    </Button>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: 'none', padding: '0 24px 20px 24px' }}>
                    <Button
                        variant="danger"
                        onClick={handleCloseModal}
                        style={{
                            backgroundColor: '#DC3545',
                            borderColor: '#DC3545',
                            color: '#FFF',
                            borderRadius: '4px',
                            fontWeight: '500',
                            padding: '6px 16px',
                            fontSize: '0.9rem'
                        }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}export default LimiteBouleTable;
